import { TableBody, TableHead, TableRow } from "@mui/material";
import React, { useCallback, useState } from "react";
import { ManagementTableColumn } from "../../shared/model/customers-table-column.model";
import {
  CustomerData,
  CustomersResponseModel,
  TransformedOrder,
  TransformedUser,
} from "../../shared/model/customers-response.model";
import {
  OrderListContainer,
  OrderTable,
  OrderTableCell,
  OrderTableRow,
} from "../../../pages/orders/my/order-list/styled";
import BarekamText from "../../../pages/components/text";
import BarekamPagination from "../../../pages/orders/my/pagination/barekam-pagination";
import Message from "../../../pages/components/message";
import { MessageSections } from "../../../pages/components/message/message";
import { useNavigate, useParams } from "react-router-dom";
import NoOrders from "../../../pages/orders/my/no-orders";
import { ConfirmationDialog } from "../../../pages/components/dialogs/confirmation-dialog";
import allUsersService from "../../../services/all-users.service";
import orderService from "../../../services/orders.service";
import { WarehouseModel } from "../../../pages/shared/models/order/warehouse.model";
import { ManagementTableRowCell } from "./management-table-row-cell";
import { ManagementPageType } from "../../user-order-management/management-data-container";
import {
  UserStatusActionMessages,
  UserStatusModel,
} from "../../../pages/shared/models/user/user-status";
import { initToast, getMessage } from "../../../pages/shared/helper-functions";
import { ToastContainer } from "react-toastify";
import { MessageModel } from "../../../pages/shared/models/message.model";
import { ActionDialog } from "../../actions/dialogs/action-dialog";
import {
  MessagesManagementActionTypes,
  StaticDataActionTypes,
} from "../../actions/action-types.enum";
import { StaticDataDialogProps } from "../../actions/dialogs/blocks/static-data/edit-static-data";
import { TabType } from "../../shared/enums";
import { useAppSelector } from "../../../redux/store";

const ManagementTable = ({
  customersData,
  columns,
  handlePageChange,
  type,
  staticDataType,
}: CustomersListProps) => {
  const { messageCache, languageId } = useAppSelector(state => state.messagesReducer);
  const [changeStatusData, setChangeStatusData] = useState<{
    userData: CustomerData;
    status: UserStatusModel;
  }>();
  const [activeAction, setActiveAction] = useState<string>();
  const [orderStatusUpdated, setOrderStatusUpdated] = useState<{ orderId: number, orderUpdateData: Record<string, number | string> } | undefined>();
  const [staticDataDialogProps, setStaticDataDialogProps] =
    useState<StaticDataDialogProps>();

  const navigate = useNavigate();
  const { warehouseId } = useParams();

  function handleRowClick(
    data: CustomerData | WarehouseModel | MessageModel | TransformedOrder | TransformedUser,
  ): void {
    if (data.hasOwnProperty("user")) {
      const customerData = data as CustomerData;

      if (type === ManagementPageType.CUSTOMERS) {
        navigate(`../customer/${customerData.user.userId}/account`);
      } else if (type === ManagementPageType.WAREHOUSE_WORKERS) {
        if (warehouseId) {
          navigate(`../worker/${customerData.user.userId}/account`);
        } else {
          navigate(`../warehouse/worker/${customerData.user.userId}/account`);
        }
      }
    } else {
      const warehouse = data as WarehouseModel;

      navigate(`../warehouse/${warehouse.warehouseId}`);
    }
  }

  function handleChangeStatus(withChange: boolean): void {
    if (withChange && changeStatusData) {
      const userId = changeStatusData.userData.user.userId;
      const statusId = changeStatusData.status.userStatusId;

      if (userId && statusId) {
        allUsersService.editUser(userId, { status: statusId }).then(() => {
          const toastMessage = "status_changed";
          const tooltipType = "success";

          initToast(toastMessage, tooltipType);
          setChangeStatusData(undefined);
          handlePageChange("", customersData.currentPage + 1);
        });
      }
    }
    setChangeStatusData(undefined);
  }

  const CustomersListTableHead = () => {
    return (
      <TableHead>
        <TableRow>
          {columns.map((column, index) => (
            <OrderTableCell key={`${index}-${column.variant}`}>
              <BarekamText
                variant={"m"}
                fontWeight={600}
                align={index === columns.length - 1 ? "right" : "left"}
              >
                <Message
                  messageKey={column.headerName}
                  sectionId={MessageSections.MAIN}
                />
              </BarekamText>
            </OrderTableCell>
          ))}
        </TableRow>
      </TableHead>
    );
  };

  function getIndex(data: any) {
    if (data.hasOwnProperty("warehouseId")) {
      return "warehouse-" + (data as WarehouseModel).warehouseId;
    } else if (data.hasOwnProperty("userId")) {
      return "customer-data-" + (data as CustomerData).user.userId;
    } else if (data.hasOwnProperty("messageId")) {
      return "message-" + (data as MessageModel).messageId;
    }
  }

  function handleActionPopupClose() {
    setActiveAction(undefined);
    setStaticDataDialogProps(undefined);
    handlePageChange("", customersData.currentPage + 1);
  }

  function onEdit(
    data: any,
    action: MessagesManagementActionTypes | StaticDataActionTypes,
  ) {
    setStaticDataDialogProps({ staticData: data, type: staticDataType });
    setActiveAction(action);
  }

  function onDelete(data: any) {
    setStaticDataDialogProps({ staticData: data, type: staticDataType });
    setActiveAction(StaticDataActionTypes.DELETE);
    // messagesService.deleteMessage(data.messageId).then(() => alert('Success'));
  }

  const orderStateUpdateHandler = useCallback((withChange: boolean) => {
    if (orderStatusUpdated?.orderId && withChange) {
      orderService.updateState(orderStatusUpdated.orderId, orderStatusUpdated?.orderUpdateData).then(resp => {
        setOrderStatusUpdated(undefined);
        const toastMessage = getMessage("status_changed", MessageSections.ORDERS, messageCache, languageId);
        const tooltipType = "success";

        initToast(toastMessage, tooltipType);
        handlePageChange("", customersData.currentPage + 1);
      });
    } else {
      setOrderStatusUpdated(undefined);
    }
  }, [orderStatusUpdated]);

  return (
    <>
      <OrderListContainer>
        {customersData?.data?.length ? (
          <OrderTable width={"100%"}>
            <CustomersListTableHead />

            <TableBody>
              {customersData.data.map((data, rowIndex) => {
                const index = `${getIndex(data)}-${rowIndex}`;

                return (
                  <OrderTableRow hover tabIndex={rowIndex} key={index}>
                    {columns.map((column, columnIndex) => (
                      <ManagementTableRowCell
                        column={column}
                        data={data}
                        clicked={handleRowClick}
                        changeStatus={(userData, status) =>
                          setChangeStatusData({
                            userData,
                            status,
                          })
                        }
                        orderStateUpdate={(orderId, orderUpdateData) => setOrderStatusUpdated({ orderId, orderUpdateData })}
                        onEdit={(data, action) =>
                          onEdit && onEdit(data, action)
                        }
                        onDelete={(data) => onDelete && onDelete(data)}
                        key={index + "-" + columnIndex}
                      />
                    ))}
                  </OrderTableRow>
                );
              })}
            </TableBody>
          </OrderTable>
        ) : (
          <NoOrders messageKey={"nothing_found"} />
        )}
      </OrderListContainer>

      {customersData.totalItems > customersData.pageSize ? (
        <BarekamPagination
          totalItems={customersData.totalItems}
          pageSize={customersData.pageSize}
          currentPage={customersData.currentPage}
          onPageChange={handlePageChange}
        />
      ) : (
        <></>
      )}

      {!!changeStatusData && (
        <ConfirmationDialog
          open={true}
          handleClose={handleChangeStatus}
          titleKey={"change_user_status"}
          descriptionKey={
            UserStatusActionMessages[changeStatusData.status.userStatusId] +
            "_description"
          }
        />
      )}

      {!!orderStatusUpdated && (
        <ConfirmationDialog
          open={true}
          handleClose={orderStateUpdateHandler}
          titleKey={"change_mobile_order_status_title"}
          descriptionKey={'change_mobile_order_status_desc'}
        />
      )}

      <ActionDialog
        open={!!activeAction}
        closePopup={() => handleActionPopupClose()}
        action={activeAction}
        staticDataDialogProps={staticDataDialogProps}
        messageSection={MessageSections.MAIN}
      />

      <ToastContainer />
    </>
  );
};

interface CustomersListProps {
  type: ManagementPageType;
  columns: ManagementTableColumn[];
  customersData: CustomersResponseModel;
  handlePageChange: (event: any, newPage: number) => void;

  staticDataType?: TabType;
}

export default ManagementTable;
